import * as React from "react";
import { DropDownList as KDropDownList } from "@progress/kendo-react-dropdowns";

import type {
  DropDownListChangeEvent as KDropDownListChangeEvent,
  DropDownListHandle as KDropDownListHandle,
  DropDownListProps as KDropDownListProps,
  ListItemProps as KListItemProps,
} from "@progress/kendo-react-dropdowns";

import "./DropDownList.scss";

export interface DropDownListProps extends Omit<KDropDownListProps, "onChange"> {
  preventNavigation?: boolean;
  returnFullRecord?: boolean;
  onChange: (fieldName: string, value: string, e: DropDownListChangeEvent) => void;
  alwaysShowTooltip: boolean;
  clearButton?: boolean;
  defaultValue?: string | null;
  dataTestId?: string;
}

export interface DropDownListKendoState {
  showTooltip: boolean;
}

export class DropDownList extends React.PureComponent<DropDownListProps, DropDownListKendoState> {
  static defaultProps = {
    alwaysShowTooltip: true,
    defaultValue: null,
  };

  dropDownList: KDropDownListHandle | null;

  constructor(opts: DropDownListProps) {
    super(opts);

    this.state = { showTooltip: this.props.alwaysShowTooltip };
  }

  componentDidUpdate(): void {
    this.showTooltipCheck();
  }

  componentDidMount(): void {
    this.showTooltipCheck();
  }

  handleClear = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (this.props.onChange && this.props.name) {
      this.props.onChange(this.props.name, this.props.defaultValue!, null as any);
    }
  };

  handleDOMClear = (e: Event): void => {
    e.stopPropagation();
    if (this.props.onChange && this.props.name) {
      this.props.onChange(this.props.name, this.props.defaultValue!, null as any);
    }
  };

  render() {
    const onKendoChange = (e: KDropDownListChangeEvent, providedChangeHandler) => {
      const value =
        this.props.dataItemKey && !this.props.returnFullRecord ?
          e.value[this.props.dataItemKey] || e.value
        : e.value;
      providedChangeHandler(this.props.name ?? e.target?.name, value, e);
    };

    // We'll use a custom class to identify dropdowns with clear buttons
    const className = `tc-ddl ${this.props.clearButton ? "tc-ddl-with-clear" : ""} ${this.props.className || ""}`;

    return (
      <KDropDownList
        id={this.props.id}
        className={className}
        data-testid={this.props.dataTestId || this.props.name}
        data={this.props.data}
        dataItemKey={this.props.dataItemKey}
        defaultItem={this.props.defaultItem}
        disabled={this.props.disabled}
        fillMode={this.props.fillMode}
        filter={this.props.filter}
        filterable={this.props.filterable}
        header={this.props.header}
        itemRender={this.props.itemRender}
        label={this.props.label}
        listNoDataRender={this.props.listNoDataRender}
        loading={this.props.loading}
        name={this.props.name}
        onBlur={this.props.onBlur}
        onChange={(e) => {
          onKendoChange(e, this.props.onChange);
        }}
        onClose={this.props.onClose}
        onFilterChange={this.props.onFilterChange}
        onOpen={this.props.onOpen}
        onPageChange={this.props.onPageChange}
        opened={this.props.opened}
        popupSettings={this.props.popupSettings}
        ref={(ref) => {
          this.DropDownListRef(ref);
        }}
        required={this.props.required}
        size={this.props.size}
        style={this.props.style}
        textField={this.props.textField}
        title={this.state.showTooltip ? this.props.title : ""}
        valid={this.props.valid}
        validationMessage={this.props.validationMessage}
        value={this.props.value}
        valueRender={this.props.valueRender}
        virtual={this.props.virtual}
      />
    );
  }

  isDropDownOpen(): boolean {
    const handle = this.dropDownList;
    return handle?.opened ?? false;
  }

  private isEllipsisVisible = (element: Element | null) => {
    if (element) {
      if (element.clientWidth < element.scrollWidth) {
        const style = window.getComputedStyle(element);
        return style?.textOverflow === "ellipsis";
      }
    }
    return false;
  };

  private showTooltipCheck = () => {
    const el = this.dropDownList?.element;
    if (el && !this.props.alwaysShowTooltip) {
      const inputElement = el.querySelector("span.k-input");
      const isEllipsisVisible = this.isEllipsisVisible(inputElement);
      this.setState({
        showTooltip: isEllipsisVisible,
      });
    }

    // Add clear button if needed
    if (this.props.clearButton && this.props.value && !this.props.disabled && el) {
      const wrapper = el.querySelector(".k-dropdown-wrap");
      if (wrapper) {
        // Check if clear button already exists
        let clearButton = wrapper.querySelector(".k-clear-value");
        if (!clearButton) {
          // Create and insert clear button before the select arrow
          clearButton = document.createElement("span");
          clearButton.className = "k-icon k-clear-value k-i-close";
          clearButton.setAttribute("role", "button");
          clearButton.setAttribute("tabindex", "-1");
          clearButton.setAttribute("title", "clear");

          const selectArrow = wrapper.querySelector(".k-select");
          if (selectArrow) {
            wrapper.insertBefore(clearButton, selectArrow);

            // Add click event listener
            clearButton.addEventListener("click", this.handleDOMClear);
          }
        }
      }
    } else if (el) {
      // Remove clear button if it exists but shouldn't
      const clearButton = el.querySelector(".k-clear-value");
      if (clearButton) {
        clearButton.removeEventListener("click", this.handleDOMClear);
        clearButton.remove();
      }
    }
  };

  private DropDownListRef = (ref: KDropDownListHandle | null) => {
    if (ref?.element && (this.props.dataTestId || this.props.name)) {
      ref.element.setAttribute("data-testid", this.props.dataTestId ?? this.props.name);
    }

    this.dropDownList = ref;
  };
}

export interface DropDownListChangeEvent extends KDropDownListChangeEvent {}

export interface ListItemProps extends KListItemProps {}
